import React, { useCallback, useState } from 'react';
import { AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
import { PRIVACY_URL } from '../data/site';

/*
 * NOTE FOR HANDOFF: the verification control below is a LOCAL visual mockup for
 * design review only. When this page is transferred into Website Forge, wire a
 * real CAPTCHA (e.g. reCAPTCHA v2 with a production site key) and verify the
 * token server-side, and connect the submit handler to the real lead endpoint.
 */

const SERVICE_OPTIONS = ['Auto Glass', 'Residential Glass', 'Glass Shower Doors', 'Commercial Glass'];
interface Values {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}
const EMPTY: Values = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: ''
};
type Errors = Partial<Record<keyof Values | 'captcha', string>>;
interface QuoteFormProps {
  idPrefix: string;
  title?: string;
  description?: string;
  className?: string;
  headingLevel?: 2 | 3;
}
const fieldClass = 'box-border block w-full rounded border border-kosin-line bg-white px-3 py-[7px] text-[16px] leading-6 text-kosin-ink placeholder:text-kosin-body/60';
const labelClass = 'block text-[13px] font-semibold text-kosin-charcoal';
export function QuoteForm({
  idPrefix,
  title = 'Request A Free Quote',
  description = '',
  className = '',
  headingLevel = 2
}: QuoteFormProps) {
  const Heading = (headingLevel === 3 ? 'h3' : 'h2') as 'h2' | 'h3';
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const id = (name: string) => `${idPrefix}-${name}`;
  const update = useCallback((field: keyof Values, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  }, []);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next: Errors = {};
    if (!values.name.trim()) next.name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    if (values.phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a 10-digit phone number.';
    if (!values.service) next.service = 'Please choose a service.';
    if (!captchaChecked) next.captcha = 'Please confirm you are not a robot.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const firstField = Object.keys(next).find(key => key !== 'captcha');
      if (firstField) document.getElementById(id(firstField))?.focus();
    }
    /* No network request is made from this design review build. */
  };
  const errorText = (key: keyof Errors) => errors[key] ? <p id={id(`${key}-error`)} role="alert" className="mt-1 flex items-start gap-1 text-[12px] text-kosin-red" data-id="element-205" data-mp="1lkbqo50vseih">
        <AlertCircleIcon className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-206" data-mp="1gn1t223c8wjk" />
        {errors[key]}
      </p> : null;
  return <div className={`box-border w-full rounded bg-white p-4 shadow-[0_10px_30px_rgba(22,22,22,0.14)] sm:p-5 ${className}`} data-id="element-207" data-mp="12jcpyk10d0u">
      <Heading className="text-[19px] font-bold leading-tight text-kosin-charcoal" data-id="element-208" data-mp="3jfh8rudp6lpq">
        {title}
      </Heading>
      {description && <p className="mt-1 text-[13px] leading-snug text-kosin-body" data-id="element-209" data-mp="3ng12enz2b774">
          {description}
        </p>}

      <form id={id('form')} className="box-border mt-3 w-full" noValidate onSubmit={handleSubmit} data-id="element-210" data-mp="3ehfpp9g9ume9">
        <div className="space-y-2" data-id="element-211" data-mp="30ho4oorc85qr">
          <div data-id="element-212" data-mp="30ifxjrtjvpdc">
            <label className={labelClass} htmlFor={id('name')} data-id="element-213" data-mp="30izhfe2rgnu0">
              Full Name
            </label>
            <input id={id('name')} name="name" type="text" autoComplete="name" required value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? id('name-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-214" data-mp="34jrruandcyrv" />
            {errorText('name')}
          </div>

          <div className="grid grid-cols-1 gap-2.5 min-[420px]:grid-cols-2" data-id="element-215" data-mp="41rlybvfmlw3">
            <div data-id="element-216" data-mp="3va5hp5cji3yh">
              <label className={labelClass} htmlFor={id('email')} data-id="element-217" data-mp="3v8kobj1yor27">
                Email Address
              </label>
              <input id={id('email')} name="email" type="email" autoComplete="email" required value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? id('email-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-218" data-mp="3r93homastnvg" />
              {errorText('email')}
            </div>
            <div data-id="element-219" data-mp="1j05n3yg6iq2g">
              <label className={labelClass} htmlFor={id('phone')} data-id="element-220" data-mp="1j0owurvoe85e">
                Phone Number
              </label>
              <input id={id('phone')} name="phone" type="tel" autoComplete="tel" required value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? id('phone-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-221" data-mp="1n0plf5chnzq6" />
              {errorText('phone')}
            </div>
          </div>

          <div data-id="element-222" data-mp="1989cxp0tj9hv">
            <label className={labelClass} htmlFor={id('service')} data-id="element-223" data-mp="1e12hocwaehty">
              What Are You Interested In?
            </label>
            <select id={id('service')} name="service" required value={values.service} onChange={e => update('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? id('service-error') : undefined} className={`mt-1 ${fieldClass}`} data-id="element-224" data-mp="gmgj44xi3578">
              <option value="" data-id="element-225" data-mp="na5267eig3sf">Select a service</option>
              {SERVICE_OPTIONS.map(option => <option key={option} value={option} data-id="element-226" data-mp="ibtt3yw1hxb9">
                  {option}
                </option>)}
            </select>
            {errorText('service')}
          </div>

          <div data-id="element-227" data-mp="3mps570d58fab">
            <label className={labelClass} htmlFor={id('message')} data-id="element-228" data-mp="3mqbp2mmctdqz">
              Message <span className="font-normal text-kosin-body" data-id="element-229" data-mp="4jnn35mlp6pj">(optional)</span>
            </label>
            <textarea id={id('message')} name="message" rows={2} value={values.message} onChange={e => update('message', e.target.value)} className={`mt-1 h-[58px] resize-y !leading-5 ${fieldClass}`} data-id="element-230" data-mp="3vpfb9zgti99p" />
          </div>
        </div>

        <div className="mt-3" data-id="element-231" data-mp="2svj93k683cuo">
          <div className="box-border flex w-full max-w-[304px] items-center justify-between gap-3 rounded border border-[#d3d3d3] bg-[#f9f9f9] px-3 py-[15px]" data-id="element-232" data-mp="2szfi8iz6dioa">
            <div className="flex items-center gap-3" data-id="element-233" data-mp="2jxpp98pnaboe">
              <input id={id('captcha')} name="captcha" type="checkbox" checked={captchaChecked} onChange={e => {
              setCaptchaChecked(e.target.checked);
              setErrors(prev => ({
                ...prev,
                captcha: undefined
              }));
            }} aria-invalid={Boolean(errors.captcha)} aria-describedby={errors.captcha ? id('captcha-error') : undefined} className="h-6 w-6 shrink-0 cursor-pointer rounded-[2px] border border-[#c1c1c1] accent-[#1a73e8]" data-id="element-234" data-mp="2ov7qse1c1omd" />
              <label htmlFor={id('captcha')} className="cursor-pointer text-[14px] text-kosin-ink" data-id="element-235" data-mp="45s4nwd26o32">
                I&apos;m not a robot
              </label>
            </div>
            <div className="flex shrink-0 flex-col items-center gap-0.5 text-[#9aa0a6]" data-id="element-236" data-mp="1vgn6xy6vo8bv">
              <RefreshCwIcon className="h-5 w-5 text-[#1a73e8]" aria-hidden="true" data-id="element-237" data-mp="1qhtqpy07i380" />
              <span className="text-[10px] leading-none" data-id="element-238" data-mp="1lk0nndu79pby">reCAPTCHA</span>
            </div>
          </div>
          {errorText('captcha')}
        </div>

        <button type="submit" className="mt-3 w-full rounded bg-kosin-red px-5 py-2.5 text-[16px] font-semibold text-white hover:bg-kosin-bright transition-colors duration-150 ease-kosin" data-id="element-239" data-mp="2j6himcfgnquj">
          Request My Free Quote
        </button>

        <p className="mt-2.5 text-[12px] text-kosin-body" data-id="element-240" data-mp="256g9yasyff4i">
          <a href={PRIVACY_URL} className="font-semibold text-kosin-charcoal underline underline-offset-2 hover:text-kosin-red transition-colors duration-150 ease-kosin" data-id="element-241" data-mp="xty09b18d7jo">
            Privacy Policy
          </a>
        </p>
      </form>
    </div>;
}